import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/convex';
import { Id } from '../../../convex/_generated/dataModel';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Save, Eye, ArrowLeft, Trash2, Upload } from 'lucide-react';
import { Footerdemo } from '../ui/footer-section';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { customStyle } from '../../lib/syntax-highlight-style';
import { DEFAULT_AUTHOR_BIO } from '@/constants/authorBio';

export default function BlogEditor() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { postId } = useParams();
  
  const isAdmin = useQuery(api.blog.checkAdminStatus);
  const blogPost = useQuery(
    api.blog.getAdminBlogPost, 
    postId ? { postId: postId as Id<"blogPosts"> } : 'skip'
  );
  
  const createBlogPost = useMutation(api.blog.createBlogPost);
  const updateBlogPost = useMutation(api.blog.updateBlogPost);
  const deleteBlogPost = useMutation(api.blog.deleteBlogPost);
  
  // For Convex storage operations
  const generateUploadUrl = useAction(api.uploadImage.generateUploadUrl);
  const getImageUrl = useAction(api.uploadImage.getImageUrl);
  
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [customSlug, setCustomSlug] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    imageUrl: '',
    imageStyle: 'cover',
    imageHeight: 'auto',
    tags: '',
    isPublished: false
  });

  useEffect(() => {
    if (blogPost) {
      setFormData({
        title: blogPost.title,
        content: blogPost.content,
        summary: blogPost.summary,
        imageUrl: blogPost.imageUrl || '',
        imageStyle: blogPost.imageStyle || 'cover',
        imageHeight: blogPost.imageHeight || 'auto',
        tags: blogPost.tags.join(', '),
        isPublished: blogPost.isPublished
      });
      setCustomSlug(blogPost.slug);
    }
  }, [blogPost]);

  // Generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  };

  // Handle title change and auto-generate slug
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setFormData(prev => ({ ...prev, title: newTitle }));
    
    // Only auto-generate slug if it hasn't been manually edited
    if (!postId && !customSlug) {
      setCustomSlug(generateSlug(newTitle));
    }
  };
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, isPublished: checked }));
  };

  // Handle featured image change
  const handleFeaturedImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      
      // Step 1: Get a secure upload URL from Convex
      const uploadUrl = await generateUploadUrl();
      console.log("Got upload URL:", uploadUrl);
      
      // Step 2: Upload the file to the upload URL
      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      
      if (!result.ok) {
        throw new Error(`Failed to upload image: ${result.status} ${result.statusText}`);
      }
      
      // Step 3: Get the storage ID from the result
      const { storageId } = await result.json();
      console.log("File uploaded, storage ID:", storageId);
      
      // Step 4: Get the proper URL for the uploaded file
      const imageUrl = await getImageUrl({ storageId });
      console.log("Generated URL:", imageUrl);
      
      if (imageUrl) {
        // Update form state with the valid image URL
        setFormData(prev => ({ ...prev, imageUrl }));
      } else {
        throw new Error("Failed to get image URL");
      }
      
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const formSubmitData = { ...formData };
    if (!postId) {
      formSubmitData.authorBio = DEFAULT_AUTHOR_BIO;
    }
    
    try {
      // Convert tags string to array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag !== '');

      // Validate slug
      if (!customSlug) {
        toast({
          title: "Error",
          description: "Please enter a URL slug for the blog post",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      
      // Validate slug format manually first to provide better error message
      const slugPattern = /^[a-z0-9-]+$/;
      if (!slugPattern.test(customSlug)) {
        toast({
          title: "Error",
          description: "Slug must contain only lowercase letters, numbers, and hyphens",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      
      if (postId) {
        // Update existing post
        await updateBlogPost({
          postId: postId as Id<"blogPosts">,
          title: formData.title,
          content: formData.content,
          summary: formData.summary,
          imageUrl: formData.imageUrl || undefined,
          imageStyle: formData.imageStyle,
          imageHeight: formData.imageHeight,
          tags: tagsArray,
          isPublished: formData.isPublished,
          slug: customSlug
        });
        
        toast({
          title: "Success",
          description: "Blog post updated successfully",
        });
      } else {
        // Create new post
        const result = await createBlogPost({
          title: formData.title,
          content: formData.content,
          summary: formData.summary,
          imageUrl: formData.imageUrl || undefined,
          imageStyle: formData.imageStyle,
          imageHeight: formData.imageHeight,
          tags: tagsArray,
          isPublished: formData.isPublished,
          slug: customSlug
        });
        
        toast({
          title: "Success",
          description: "Blog post created successfully",
        });
        
        // Navigate to edit page for the new post
        navigate(`/admin/blog/edit/${result.postId}`);
      }
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save blog post",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle delete post
  const handleDelete = async () => {
    if (!postId) return;
    
    if (!window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      await deleteBlogPost({
        postId: postId as Id<"blogPosts">
      });
      
      toast({
        title: "Success",
        description: "Blog post deleted successfully",
      });
      
      navigate('/admin/blog');
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete blog post",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // If not an admin, show access denied
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex flex-col pt-16">
        <div className="flex-1 max-w-7xl mx-auto px-4 py-8">
          <div className="p-8 bg-red-50 border border-red-200 rounded-lg text-center">
            <h1 className="text-2xl font-bold text-red-700 mb-4">Access Denied</h1>
            <p className="text-red-600">You don't have permission to access this area.</p>
          </div>
        </div>
        <Footerdemo />
      </div>
    );
  }
  
  // Show loading state
  if (postId && !blogPost && isAdmin !== false) {
    return (
      <div className="min-h-screen flex flex-col pt-16">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
        <Footerdemo />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col pt-16">
      <div className="flex-1 max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin/blog')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Posts
            </Button>
            <h1 className="text-2xl font-bold">
              {postId ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h1>
          </div>
          
          {postId && (
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleDelete}
              disabled={isLoading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Post
            </Button>
          )}
        </div>
        
        <Tabs defaultValue="edit" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="edit">Edit</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          
          <TabsContent value="edit">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="md:col-span-3 space-y-6">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input 
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleTitleChange}
                      required
                      placeholder="Blog Post Title"
                    />
                  </div>

                  <div>
                    <Label htmlFor="slug">URL Slug</Label>
                    <Input 
                      id="slug"
                      value={customSlug}
                      onChange={(e) => setCustomSlug(e.target.value)}
                      required
                      placeholder="custom-url-slug"
                      pattern="[a-z0-9\-]+"
                      title="Only lowercase letters, numbers, and hyphens are allowed"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This will be the URL of your blog post: aiseobloggenerator.com/blog/<strong>{customSlug || 'custom-url-slug'}</strong>
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="summary">Summary (SEO Description)</Label>
                    <textarea
                      id="summary"
                      name="summary"
                      value={formData.summary}
                      onChange={handleChange}
                      required
                      placeholder="A brief summary of the blog post (will be used as meta description)"
                      className="w-full min-h-[100px] p-2 border rounded-md"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="content">Content (Markdown)</Label>
                    <textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleChange}
                      required
                      placeholder="Write your blog post content in Markdown format..."
                      className="w-full min-h-[500px] p-4 border rounded-md font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Use Markdown syntax for formatting. For example, use # for headings, ** for bold, * for italic, etc.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <Label>Featured Image</Label>
                    <div className="mt-2 space-y-4">
                      {formData.imageUrl && (
                        <div className="rounded-md overflow-hidden border">
                          <img 
                            src={formData.imageUrl} 
                            alt="Featured" 
                            className="w-full h-40 object-cover"
                          />
                        </div>
                      )}
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFeaturedImageChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          disabled={uploadingImage}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              {formData.imageUrl ? 'Change Image' : 'Upload Image'}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Image Style</Label>
                    <select
                      value={formData.imageStyle}
                      onChange={(e) => setFormData(prev => ({ ...prev, imageStyle: e.target.value }))}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="cover">Cover</option>
                      <option value="contain">Contain</option>
                      <option value="fill">Fill</option>
                      <option value="none">None</option>
                    </select>
                  </div>

                  <div>
                    <Label>Image Height</Label>
                    <Input 
                      value={formData.imageHeight}
                      onChange={(e) => setFormData(prev => ({ ...prev, imageHeight: e.target.value }))}
                      placeholder="auto, 300px, etc."
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tags">Tags (comma separated)</Label>
                    <Input 
                      id="tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleChange}
                      placeholder="tag1, tag2, tag3"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isPublished"
                      checked={formData.isPublished}
                      onCheckedChange={handleCheckboxChange}
                    />
                    <Label htmlFor="isPublished">Publish Post</Label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={isLoading || uploadingImage}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Post
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </TabsContent>
          
          <TabsContent value="preview">
            <div className="bg-white rounded-lg shadow p-8">
              {formData.imageUrl && (
                <div className="mb-6 -mx-8 -mt-8">
                  <img 
                    src={formData.imageUrl} 
                    alt={formData.title}
                    className="w-full h-72 object-cover rounded-t-lg"
                  />
                </div>
              )}
              
              <h1 className="text-3xl font-bold mb-4">{formData.title}</h1>
              
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {formData.tags.split(',').map((tag, index) => (
                    tag.trim() && (
                      <span 
                        key={index}
                        className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                      >
                        {tag.trim()}
                      </span>
                    )
                  ))}
                </div>
              </div>
              
              <div className="prose max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({node, children, ...props}) => (
                      <h1 className="text-4xl font-bold mt-8 mb-4" {...props}>
                        {children}
                      </h1>
                    ),
                    h2: ({node, children, ...props}) => (
                      <h2 className="text-3xl font-semibold mt-8 mb-4" {...props}>
                        {children}
                      </h2>
                    ),
                    h3: ({node, children, ...props}) => (
                      <h3 className="text-2xl font-semibold mt-6 mb-3" {...props}>
                        {children}
                      </h3>
                    ),
                    p: ({node, children, ...props}) => (
                      <p className="my-4 text-lg leading-relaxed text-gray-800" {...props}>
                        {children}
                      </p>
                    ),
                    ul: ({node, children, ...props}) => (
                      <ul className="my-6 ml-6 list-disc space-y-2" {...props}>
                        {children}
                      </ul>
                    ),
                    ol: ({node, children, ...props}) => (
                      <ol className="my-6 ml-6 list-decimal space-y-2" {...props}>
                        {children}
                      </ol>
                    ),
                    li: ({node, children, ...props}) => (
                      <li className="text-lg text-gray-800" {...props}>
                        {children}
                      </li>
                    ),
                    blockquote: ({node, children, ...props}) => (
                      <blockquote className="border-l-4 border-blue-500 pl-4 my-6 italic text-gray-700" {...props}>
                        {children}
                      </blockquote>
                    ),
                    code: ({node, inline, className, children, ...props}) => {
                      const match = /language-(\w+)/.exec(className || '');
                      const language = match ? match[1] : '';
                      
                      return !inline ? (
                        <SyntaxHighlighter
                          style={customStyle}
                          language={language || 'javascript'}
                          PreTag="div"
                          className="rounded-lg !my-6 border !bg-white"
                          showLineNumbers={true}
                          customStyle={{
                            margin: '1.5rem 0',
                            padding: '1rem',
                            fontSize: '0.875rem',
                            lineHeight: '1.5',
                            background: 'white',
                          }}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className="bg-gray-100 text-blue-600 rounded px-1 py-0.5 text-sm font-mono" {...props}>
                          {children}
                        </code>
                      );
                    },
                    a: ({node, children, ...props}) => (
                      <a className="text-blue-600 hover:text-blue-800 underline" {...props}>
                        {children}
                      </a>
                    ),
                    img: ({node, ...props}) => (
                      <img className="rounded-lg shadow-md my-8" {...props} />
                    ),
                    table: ({node, children, ...props}) => (
                      <div className="overflow-x-auto my-8">
                        <table className="min-w-full border-collapse border border-gray-200 rounded-lg" {...props}>
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({node, children, ...props}) => (
                      <thead className="bg-gray-50" {...props}>
                        {children}
                      </thead>
                    ),
                    tbody: ({node, children, ...props}) => (
                      <tbody className="divide-y divide-gray-200 bg-white" {...props}>
                        {children}
                      </tbody>
                    ),
                    tr: ({node, children, ...props}) => (
                      <tr className="hover:bg-gray-50 transition-colors" {...props}>
                        {children}
                      </tr>
                    ),
                    th: ({node, children, ...props}) => (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200" {...props}>
                        {children}
                      </th>
                    ),
                    td: ({node, children, ...props}) => (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200 text-center" {...props}>
                        {children}
                      </td>
                    ),
                  }}
                >
                  {formData.content}
                </ReactMarkdown>
              </div>
              
              <div className="mt-8 pt-4 border-t">
                <h3 className="text-lg font-semibold mb-2">SEO Preview</h3>
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <p className="text-lg text-blue-600 underline mb-1">{formData.title}</p>
                  <p className="text-sm text-gray-800">{formData.summary}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button 
                type="button"
                onClick={() => setActiveTab('edit')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Back to Editor
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footerdemo />
    </div>
  );
}